/**
 * Backend Policy
 *
 * Reacts when a new user message is added.
 * Calls the AI service to trigger the backend flow, then subscribes to
 * WebSocket StreamUpdate messages for incremental response processing.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { AIAssistantState } from '../state';
import { AIAssistantCommand } from '../commands';
import { AIAssistantService } from '@infrastructure/aiAssistant/aiAssistantService';
import { buildAppContext } from '@infrastructure/aiAssistant/contextBuilder';
import { RealtimeSession } from '@bett3r-dev/teselly-client-library';
import { subscribeToAssistantStream } from './streamSubscription';

export const createBackendPolicy = (
  service: AIAssistantService,
  realtimeSession: RealtimeSession | null
): Policy<AIAssistantState, AIAssistantCommand> => ({
  name: 'ai-backend',

  shouldReact: ( prev, next ) => {
    // React when processing starts (new user message added with processing flag)
    if ( !prev.isProcessing && next.isProcessing ) return true;
    return false;
  },

  onStateChange: ({ next, ctx }) => {
    if ( !next.isProcessing ) return;

    /**
     * The LAST loading assistant message — the one this turn just added.
     *
     * ⚠️ This was `.find()`, which returns the FIRST match. Any message left
     * loading by an earlier turn (aborting a response used to leave one; see
     * `message/settleLoading`) therefore captured every later answer: the
     * stream chunks and the final response were written into that old bubble,
     * so instead of a new message appearing, a previous one silently rewrote
     * itself. Searching backwards makes the newest bubble win even if a stale
     * one survives.
     *
     * `findLast` is ES2023 and this app targets ES2020, hence the loop.
     */
    let loadingMsg: typeof next.messages[number] | undefined;
    for ( let i = next.messages.length - 1; i >= 0; i-- ) {
      const candidate = next.messages[i];
      if ( candidate.sender === 'assistant' && candidate.isLoading ) {
        loadingMsg = candidate;
        break;
      }
    }
    if ( !loadingMsg ) return;

    // Find the latest user message
    const userMessages = next.messages.filter( m => m.sender === 'user' );
    const lastUserMessage = userMessages[userMessages.length - 1];
    if ( !lastUserMessage ) return;

    ctx.dispatch(
      { type: 'aiAssistant/processing/setStep', payload: { step: 'analyzing' }},
      { source: 'application' }
    );

    /**
     * Outgoing app context, plus the one thing the wire cannot otherwise carry.
     *
     * ─── 🚨 A WORKAROUND, NOT A DESIGN (TV1-2340 · P5) ────────────────────
     * The backend's conversation history persists `{ role, content }` STRINGS
     * only, and the client sends just the latest message. So on turn 2 of a
     * refinement ("no, sólo para el canal X") the model has NO record of the
     * rule graph it drew on turn 1 and recomposes from scratch — which reads
     * as the model being weak and is actually a schema gap.
     *
     * Re-injecting the graph through `context.storeStates` closes it for the
     * demo without a schema change on either side: `storeStates` is already a
     * free-form `Record<string, unknown>` that rides the wire as
     * `storeStatesSummary`, and putting it there does not corrupt the user's
     * own message text the way appending to `message` would.
     *
     * What this does NOT do, plainly, so nobody mistakes it for the fix:
     *   • it is IN-MEMORY — a page reload loses it;
     *   • a RESUMED conversation (loaded from history) starts without it;
     *   • nothing on the backend validates or persists it.
     * The correct fix is extending the backend conversation-history schema to
     * carry structured artifacts alongside the text — a domain schema change
     * with a persisted-event migration. Explicitly out of scope here.
     */
    const appContext = buildAppContext();
    const context = next.lastComposedGraph
      ? {
        ...appContext,
        storeStates: {
          ...appContext.storeStates,
          aiAssistantLastComposedGraph: next.lastComposedGraph
        }
      }
      : appContext;

    // Ensure conversation exists
    const ensureConversation = next.conversationId
      ? Promise.resolve( next.conversationId )
      : service.createConversation().then( result => {
        ctx.dispatch(
          { type: 'aiAssistant/conversation/setId', payload: { id: result.id }},
          { source: 'application' }
        );
        return result.id;
      });

    ensureConversation.then( convId => {
      // Subscribe to WebSocket stream updates before sending the message
      // so we do not miss any early updates. The handler self-unsubscribes on
      // the terminal updates (done / error / interrupt).
      const unsubscribe = subscribeToAssistantStream({
        realtimeSession,
        conversationId: convId,
        loadingMessageId: loadingMsg.id,
        ctx
      });

      ctx.dispatch(
        { type: 'aiAssistant/processing/setStep', payload: { step: 'formulating' }},
        { source: 'application' }
      );

      // POST the message to trigger the backend event-sourced flow.
      // The actual response content arrives via the WebSocket subscription above.
      return service.sendMessage({
        conversationId: convId,
        message: lastUserMessage.content,
        context
      }).catch( error => {
        console.error( '[ai-backend] Error sending message:', error );
        unsubscribe();

        ctx.dispatch(
          { type: 'aiAssistant/message/setAssistantResponse', payload: {
            messageId: loadingMsg.id,
            content: 'Sorry, I encountered an error processing your request. Please try again.'
          }},
          { source: 'api' }
        );

        ctx.dispatch(
          { type: 'aiAssistant/processing/setProcessing', payload: { isProcessing: false }},
          { source: 'application' }
        );

        ctx.dispatch(
          { type: 'aiAssistant/processing/setStep', payload: { step: '' }},
          { source: 'application' }
        );
      });
    }).catch( error => {
      console.error( '[ai-backend] Error creating conversation:', error );

      ctx.dispatch(
        { type: 'aiAssistant/message/setAssistantResponse', payload: {
          messageId: loadingMsg.id,
          content: 'Sorry, I encountered an error processing your request. Please try again.'
        }},
        { source: 'api' }
      );

      ctx.dispatch(
        { type: 'aiAssistant/processing/setProcessing', payload: { isProcessing: false }},
        { source: 'application' }
      );

      ctx.dispatch(
        { type: 'aiAssistant/processing/setStep', payload: { step: '' }},
        { source: 'application' }
      );
    });
  }
});
